/**
 * The Corteva lockup, at a height you choose.
 *
 * The geometry has exactly one home — `src/assets/corteva-lockup.svg`, which also
 * records how it was derived and that it is an approximation of the official
 * artwork. This component only sizes it and colours it.
 *
 * Inlined via Vite's `?raw` and `dangerouslySetInnerHTML` rather than rendered as
 * `<img src={…}>`, and the reason is the whole point of the asset: the wordmark is
 * `fill="currentColor"` so that it is `text-slate-100` on the dark page and
 * `text-slate-900` on the light one. An `<img>` is a separate document and inherits
 * nothing, so it would need two files and would go wrong the next time only one of
 * them was updated. The alternative that keeps `currentColor` — `vite-plugin-svgr`
 * — is a new dependency, and `ui/package.json` is frozen.
 *
 * `dangerously` is accurate about the API and not about the risk here: the string is
 * a file in this repository, resolved at build time, and no runtime value reaches it.
 *
 * Sized by **height**, in px. The lockup is 5:1, so a width prop would be the wrong
 * axis: what has to line up is the cap height against the text beside it.
 */
import lockupSvg from '../assets/corteva-lockup.svg?raw'
import { cn } from '../lib/utils'

/**
 * The asset's explanatory comments are longer than its path data and none of it
 * belongs in the DOM. Stripped once at module scope rather than per render.
 *
 * The bundle still carries them — `?raw` is the file, verbatim — which is ~4 kB
 * before gzip and is the price of the geometry having one home that can explain
 * itself.
 */
const LOCKUP = lockupSvg.replace(/<!--[\s\S]*?-->/g, '').trim()

interface Props {
  /** Rendered height in px; the width follows from the 928×187 viewBox. */
  size?: number
  className?: string
}

export function Logo({ size = 20, className }: Props) {
  return (
    // `role="img"` with a name on the wrapper, which makes the injected `<svg>` and
    // its two paths presentational — the alternative, `aria-hidden` inside the asset
    // file, would be a decision about accessibility stored in an image.
    //
    // The name is "Corteva" and not "Corteva logo": a screen reader already
    // announces "image".
    <span
      role="img"
      aria-label="Corteva"
      style={{ height: size }}
      // `inline-flex`, not `inline-block`: the width has to come from the 928×187
      // viewBox rather than from a second copy of that ratio in TypeScript, so the
      // `<svg>` is `h-full w-auto` and resolves its own width from the aspect. As a
      // flex item that is the ordinary intrinsic-size path; as a block child it
      // relies on the SVG default sizing algorithm, which browsers have been less
      // consistent about.
      className={cn(
        'inline-flex shrink-0 items-center [&>svg]:h-full [&>svg]:w-auto',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: LOCKUP }}
    />
  )
}
